import React, { useEffect, useState } from 'react';
import { apiRequest } from '../api';
import { useApp } from '../App';

export default function LoginPage() {
  const {
    authMode, setAuthMode,
    loginForm, setLoginForm,
    registerForm, setRegisterForm,
    resetRequestForm, setResetRequestForm,
    resetConfirmForm, setResetConfirmForm,
    loginInviteForm, setLoginInviteForm,
    selectedPlan,
    authBusy,
    authMessage, authError,
    setAuthMessage,
    setAuthError,
    mfaChallengeRequired, setMfaChallengeRequired,
    otpChallengeRequired, setOtpChallengeRequired,
    showRecoveryLoginInput, setShowRecoveryLoginInput,
    oidcProvider, oidcCode, setOidcCode,
    catalog,
    otpForm, setOtpForm,
    otpDebugCode,
    loginOtpDebugCode,
    handleLogin,
    handleSendLoginOtp,
    handleRegister,
    handleCompleteRegister,
    handleResendRegisterOtp,
    handlePasswordResetRequest,
    handlePasswordResetConfirm,
    handleAcceptInvitationFromLogin,
    handleSendInvitationJoinOtp,
    handleOidcStart,
    handleOidcCodeExchange,
    resetMessages,
  } = useApp();

  const [resendCooldown, setResendCooldown] = useState(0);
  const [showInvitationJoin, setShowInvitationJoin] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const inviteToken = (params.get('invite_token') || '').trim();
    const resetToken = (params.get('reset_token') || '').trim();
    const flow = (params.get('flow') || '').trim();
    if (flow === 'reset' && resetToken) {
      setShowInvitationJoin(false);
      setAuthMode('reset-confirm');
      setResetConfirmForm((prev) => ({ ...prev, token: resetToken }));
      setAuthMessage('Enter your new password to finish resetting your account.');
      return;
    }

    if (!(flow === 'invite' && inviteToken)) return;

    let cancelled = false;
    async function prepareInvitation() {
      setAuthMode('login');
      setLoginInviteForm((prev) => ({ ...prev, token: inviteToken }));
      try {
        const preview = await apiRequest(`/api/v1/cloud-accounts/invitations/preview-public?token=${encodeURIComponent(inviteToken)}`);
        if (cancelled) return;
        if (preview.is_actionable) {
          setShowInvitationJoin(true);
          setAuthMessage('Complete invitation verification to join this workspace.');
          return;
        }
        setShowInvitationJoin(false);
        setAuthError(preview.message || 'This invitation cannot be accepted.');
      } catch (error) {
        if (cancelled) return;
        setShowInvitationJoin(false);
        setAuthError(error.message || 'Invitation link is invalid or no longer active.');
      }
    }

    prepareInvitation();
    return () => {
      cancelled = true;
    };
  }, [setAuthError, setAuthMessage, setAuthMode, setLoginInviteForm, setResetConfirmForm]);

  return (
    <div className="login-page">
      <div className="ambient ambient-left" />
      <div className="ambient ambient-right" />

      <header className="topbar topbar-slim login-topbar">
        <a className="brand" href="/">
          <img className="brand-mark" src="/SROIK-LOGO.PNG" alt="Sroik" />
        </a>
        <a className="secondary-action" href="/">Back to site</a>
      </header>

      <main className="login-main">
        <div className="login-card">
          <div className="auth-tabs">
            <button
              className={authMode === 'login' ? 'auth-tab active' : 'auth-tab'}
              onClick={() => {
                setAuthMode('login');
                setMfaChallengeRequired(false);
                setOtpChallengeRequired(false);
                setShowRecoveryLoginInput(false);
                resetMessages();
              }}
              type="button"
            >
              Sign in
            </button>
            <button
              className={authMode === 'register' || authMode === 'register-otp' ? 'auth-tab active' : 'auth-tab'}
              onClick={() => { setAuthMode('register'); setMfaChallengeRequired(false); setShowRecoveryLoginInput(false); resetMessages(); }}
              type="button"
            >
              Register
            </button>
            <button
              className={authMode === 'reset-request' || authMode === 'reset-confirm' ? 'auth-tab active' : 'auth-tab'}
              onClick={() => { setAuthMode('reset-request'); setMfaChallengeRequired(false); resetMessages(); }}
              type="button"
            >
              Reset password
            </button>
          </div>

          {authMode === 'login' && (
            <form className="auth-form" onSubmit={handleLogin}>
              <label>
                Work email
                <input value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} type="email" required />
              </label>
              <label>
                Password
                <input value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} type="password" required />
              </label>
              {!mfaChallengeRequired && !otpChallengeRequired && (
                <>
                  <p className="form-hint">Use your work email and password. We will prompt OTP or MFA as needed.</p>
                  <button className="primary-action full-width" disabled={authBusy} type="submit">
                    {authBusy ? 'Checking credentials...' : 'Continue'}
                  </button>
                </>
              )}
              {otpChallengeRequired && !mfaChallengeRequired && (
                <>
                  <label>
                    Email verification code
                    <input
                      value={loginForm.otp_code}
                      onChange={(e) => setLoginForm({ ...loginForm, otp_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      maxLength="6"
                      required
                    />
                  </label>
                  {loginOtpDebugCode && (
                    <p className="form-hint otp-debug-hint">Debug: your login code is <strong>{loginOtpDebugCode}</strong></p>
                  )}
                  <button className="secondary-action" type="button" onClick={handleSendLoginOtp} disabled={authBusy || !loginForm.email}>
                    {authBusy ? 'Sending code...' : 'Send login verification code'}
                  </button>
                  <p className="form-hint">Sign in requires password + email verification code when MFA is not enabled.</p>
                  <button className="secondary-action" type="button" onClick={() => { setOtpChallengeRequired(false); resetMessages(); }}>
                    Back
                  </button>
                  <button className="primary-action full-width" disabled={authBusy || loginForm.otp_code.length < 6} type="submit">
                    {authBusy ? 'Signing in...' : 'Verify code and sign in'}
                  </button>
                </>
              )}
              {mfaChallengeRequired && (
                <>
                  {authMessage && <p className="auth-feedback auth-success">{authMessage}</p>}
                  {authError && <p className="auth-feedback auth-error">{authError}</p>}
                  <label>
                    Authenticator code
                    <input value={loginForm.authenticator_code} onChange={(e) => setLoginForm({ ...loginForm, authenticator_code: e.target.value.replace(/\D/g, '').slice(0, 6) })} type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" maxLength="6" />
                  </label>
                  <p className="form-hint">Codes rotate every 30 seconds. If one just expired, enter the newest 6-digit code and sign in again.</p>
                  {!showRecoveryLoginInput && (
                    <button className="secondary-action" type="button" onClick={() => setShowRecoveryLoginInput(true)}>
                      Use a recovery code instead
                    </button>
                  )}
                  {showRecoveryLoginInput && (
                    <>
                      <label>
                        Recovery code
                        <input value={loginForm.recovery_code} onChange={(e) => setLoginForm({ ...loginForm, recovery_code: e.target.value.toUpperCase() })} type="text" placeholder="ABCD-EFGH" />
                      </label>
                      <button className="secondary-action" type="button" onClick={() => { setShowRecoveryLoginInput(false); setLoginForm({ ...loginForm, recovery_code: '' }); }}>
                        Back to authenticator code
                      </button>
                    </>
                  )}
                </>
              )}
              {mfaChallengeRequired && (
                <button className="primary-action full-width" disabled={authBusy} type="submit">
                  {authBusy ? 'Signing in...' : 'Sign in'}
                </button>
              )}
            </form>
          )}

          {authMode === 'register' && (
            <form className="auth-form" onSubmit={handleRegister}>
              <label>
                Company or tenant name
                <input value={registerForm.tenant_name} onChange={(e) => setRegisterForm({ ...registerForm, tenant_name: e.target.value })} type="text" required />
              </label>
              <label>
                Full name
                <input value={registerForm.full_name} onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })} type="text" />
              </label>
              <label>
                Work email
                <input value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} type="email" required />
              </label>
              <label>
                Password
                <input value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} type="password" minLength="10" required />
              </label>
              <label>
                Plan
                <select value={registerForm.plan} onChange={(e) => setRegisterForm({ ...registerForm, plan: e.target.value })}>
                  {(catalog.plans || []).map((plan) => (
                    <option key={plan.key} value={plan.key}>{plan.name}</option>
                  ))}
                </select>
              </label>
              {selectedPlan && <p className="form-hint">Selected plan: {selectedPlan.audience}</p>}
              <button className="primary-action full-width" disabled={authBusy} type="submit">
                {authBusy ? 'Sending verification code...' : 'Continue with email verification'}
              </button>
            </form>
          )}

          {authMode === 'register-otp' && (
            <form className="auth-form" onSubmit={handleCompleteRegister}>
              <p className="form-hint">A 6-digit verification code was sent to <strong>{registerForm.email}</strong>. Enter it below to create your workspace.</p>
              {otpDebugCode && (
                <p className="form-hint otp-debug-hint">Debug: your code is <strong>{otpDebugCode}</strong></p>
              )}
              <label>
                Verification code
                <input
                  value={otpForm.otp_code}
                  onChange={(e) => setOtpForm({ otp_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  maxLength="6"
                  required
                  autoFocus
                />
              </label>
              <button className="primary-action full-width" disabled={authBusy || otpForm.otp_code.length < 6} type="submit">
                {authBusy ? 'Creating workspace...' : 'Verify and create workspace'}
              </button>
              <button
                className="secondary-action"
                type="button"
                disabled={authBusy || resendCooldown > 0}
                onClick={async () => {
                  const resent = await handleResendRegisterOtp();
                  if (resent) {
                    setResendCooldown(30);
                  }
                }}
              >
                {resendCooldown > 0 ? `Resend verification code in ${resendCooldown}s` : 'Resend verification code'}
              </button>
              <button
                className="secondary-action"
                type="button"
                disabled={authBusy}
                onClick={() => { resetMessages(); setAuthMode('register'); }}
              >
                Back
              </button>
            </form>
          )}

          {authMode === 'reset-request' && (
            <form className="auth-form" onSubmit={handlePasswordResetRequest}>
              <label>
                Work email
                <input value={resetRequestForm.email} onChange={(e) => setResetRequestForm({ email: e.target.value })} type="email" required />
              </label>
              <button className="primary-action full-width" disabled={authBusy} type="submit">
                {authBusy ? 'Submitting...' : 'Request password reset'}
              </button>
            </form>
          )}

          {authMode === 'reset-confirm' && (
            <form className="auth-form" onSubmit={handlePasswordResetConfirm}>
              <label>
                Reset token
                <input value={resetConfirmForm.token} onChange={(e) => setResetConfirmForm({ ...resetConfirmForm, token: e.target.value })} type="text" required />
              </label>
              <label>
                New password
                <input value={resetConfirmForm.new_password} onChange={(e) => setResetConfirmForm({ ...resetConfirmForm, new_password: e.target.value })} type="password" minLength="10" required />
              </label>
              <button className="primary-action full-width" disabled={authBusy} type="submit">
                {authBusy ? 'Updating...' : 'Set new password'}
              </button>
            </form>
          )}

          {authMessage && !mfaChallengeRequired && <p className="auth-feedback auth-success">{authMessage}</p>}
          {authError && !mfaChallengeRequired && <p className="auth-feedback auth-error">{authError}</p>}

          {oidcProvider?.enabled && (
            <div className="oidc-block">
              <div className="login-divider"><span>or</span></div>
              <button className="secondary-action full-width" onClick={handleOidcStart} type="button">
                Continue with SSO
              </button>
              <form className="auth-form" onSubmit={handleOidcCodeExchange}>
                <label>
                  OIDC authorization code
                  <input value={oidcCode} onChange={(e) => setOidcCode(e.target.value)} type="text" required />
                </label>
                <button className="secondary-action" type="submit" disabled={authBusy}>Exchange code</button>
              </form>
              {oidcProvider?.message && <p className="form-hint">{oidcProvider.message}</p>}
            </div>
          )}

          <div className="auth-legal-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="/liability-disclaimer">Liability disclaimer</a>
          </div>
        </div>

        <aside className="login-sidecar">
          <p className="feature-eyebrow">Enterprise controls</p>
          <h3>Start with enterprise-grade access patterns.</h3>
          <ul className="card-list compact-list">
            <li>Register a tenant with a selected plan.</li>
            <li>Use password reset workflows for account recovery.</li>
            <li>Turn on authenticator MFA and store recovery codes.</li>
            <li>Review SOC-oriented controls in the compliance section.</li>
          </ul>
          <a className="secondary-action" href="/api-reference">Inspect auth API endpoints</a>
        </aside>
      </main>

      {showInvitationJoin && (
        <div className="setup-modal-overlay" role="dialog" aria-modal="true" aria-label="Accept invitation">
          <div className="setup-modal setup-help-modal">
            <div className="setup-modal-header">
              <h3>Accept Invitation</h3>
              <button
                className="secondary-action"
                type="button"
                onClick={() => setShowInvitationJoin(false)}
              >
                Close
              </button>
            </div>
            <form className="auth-form" onSubmit={handleAcceptInvitationFromLogin}>
              {authMessage && <p className="auth-feedback auth-success">{authMessage}</p>}
              {authError && <p className="auth-feedback auth-error">{authError}</p>}
              <label>
                Invitation token
                <input
                  value={loginInviteForm.token}
                  onChange={(e) => setLoginInviteForm({ ...loginInviteForm, token: e.target.value })}
                  type="text"
                  placeholder="sroik_inv_..."
                  required
                />
              </label>
              <button className="secondary-action" type="button" onClick={handleSendInvitationJoinOtp} disabled={authBusy || !loginInviteForm.token.trim()}>
                {authBusy ? 'Sending verification code...' : 'Send verification code'}
              </button>
              <label>
                Email verification code
                <input
                  value={loginInviteForm.otp_code}
                  onChange={(e) => setLoginInviteForm({ ...loginInviteForm, otp_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  maxLength="6"
                  required
                />
              </label>
              <label>
                Full name (first-time users)
                <input
                  value={loginInviteForm.full_name}
                  onChange={(e) => setLoginInviteForm({ ...loginInviteForm, full_name: e.target.value })}
                  type="text"
                  placeholder="Optional"
                />
              </label>
              <label>
                Password (first-time users)
                <input
                  value={loginInviteForm.password}
                  onChange={(e) => setLoginInviteForm({ ...loginInviteForm, password: e.target.value })}
                  type="password"
                  minLength="10"
                  placeholder="Required only if this email has no account yet"
                />
              </label>
              <button className="secondary-action full-width" type="submit" disabled={authBusy || !loginInviteForm.token.trim() || loginInviteForm.otp_code.length < 6}>
                {authBusy ? 'Accepting invitation...' : 'Accept invitation and join workspace'}
              </button>
              <p className="form-hint">Do not register a new tenant for invitations. Use token + email verification + password setup here.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
